import React from 'react';

import { ActivityDescription } from './ActivityDescription';
import { formatFieldName } from './utils';

export interface FieldsChangedProps<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  previous: T | null | undefined;
  current: T | null | undefined;
  fieldsToCheck?: Array<keyof T>;
  renderField?: (name: keyof T, value: T[keyof T]) => React.ReactNode;
}

/**
 * Generic component that renders only the fields that changed between previous and current.
 * Shows the current (new) values for changed fields.
 */
export const FieldsChanged = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  previous,
  current,
  fieldsToCheck,
  renderField,
}: FieldsChangedProps<T>) => {
  if (!current) return null;

  // If no previous, show all current fields (initial population)
  if (!previous) {
    const fields = fieldsToCheck || Object.keys(current);
    const nonNullFields = fields.filter(
      (key) => current[key] !== null && current[key] !== undefined,
    );

    if (nonNullFields.length === 0) return null;

    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500 italic">Initial values set:</div>
        <div className="space-y-1">
          {nonNullFields.map((key) =>
            renderField ? (
              <div key={String(key)}>{renderField(key, current[key])}</div>
            ) : (
              <DefaultFieldDisplay
                key={String(key)}
                name={key}
                value={current[key]}
              />
            ),
          )}
        </div>
      </div>
    );
  }

  // Determine which fields to check
  const fields = fieldsToCheck || [
    ...new Set([...Object.keys(previous), ...Object.keys(current)]),
  ];

  const changedFields = fields
    .map((key) => {
      const prevValue = previous[key];
      const currValue = current[key];

      // Check if value changed
      const hasChanged =
        JSON.stringify(prevValue) !== JSON.stringify(currValue);

      if (!hasChanged) return null;

      // Skip if current value is null/undefined
      if (currValue === null || currValue === undefined) return null;

      // Skip empty arrays
      if (Array.isArray(currValue) && currValue.length === 0) return null;

      // Skip empty strings
      if (typeof currValue === 'string' && currValue.trim() === '') return null;

      return { name: key, value: currValue };
    })
    .filter((field): field is NonNullable<typeof field> => field !== null);

  if (changedFields.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">No visible changes</div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-700">Updated fields:</div>
      <div className="space-y-1">
        {changedFields.map((field) =>
          renderField ? (
            <div key={String(field.name)}>
              {renderField(field.name, field.value)}
            </div>
          ) : (
            <DefaultFieldDisplay
              key={String(field.name)}
              name={field.name}
              value={field.value}
            />
          ),
        )}
      </div>
    </div>
  );
};

/**
 * Default field renderer - shows field name and value
 */
const DefaultFieldDisplay = <T extends Record<string, unknown>>({
  name,
  value,
}: {
  name: keyof T;
  value: T[keyof T];
}) => {
  const label = formatFieldName(String(name));

  // Handle arrays
  if (Array.isArray(value)) {
    return (
      <div className="space-y-1">
        <div className="font-medium text-sm text-gray-700">{label}</div>
        <div className="pl-4">
          {value.map((item: unknown, idx) => (
            <ActivityDescription key={idx}>
              {typeof item === 'object' && item !== null
                ? JSON.stringify(item)
                : String(item)}
            </ActivityDescription>
          ))}
        </div>
      </div>
    );
  }

  // Handle objects
  if (typeof value === 'object' && value !== null) {
    const objValue = value as Record<string, unknown>;
    return (
      <div className="space-y-1">
        <div className="font-medium text-sm text-gray-700">{label}</div>
        <div className="pl-4 space-y-0.5 text-sm text-gray-600">
          {Object.entries(objValue).map(([key, val]) => (
            <ActivityDescription key={key}>
              {formatFieldName(key)}: {String(val)}
            </ActivityDescription>
          ))}
        </div>
      </div>
    );
  }

  // Handle primitives
  return (
    <ActivityDescription>
      <span className="font-medium">{label}:</span> {String(value)}
    </ActivityDescription>
  );
};
